import { CheckIcon, CopyIcon } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { useCopyToClipboard } from '@/hooks/copy-to-clipboard';

import type { NewParentDraft } from './CreateUserParentSection';

/**
 * Props for the CreatedParentAccounts component.
 * @property parents - Parent accounts created alongside the student, with the
 * passwords they were created with.
 */
interface CreatedParentAccountsProps {
  parents: NewParentDraft[];
}

/**
 * Success-page summary of the parent accounts created together with a
 * student, with a copy-password button per account.
 */
export function CreatedParentAccounts({ parents }: CreatedParentAccountsProps) {
  const [copiedEmail, setCopiedEmail] = useState<string | null>(null);
  const [, copy] = useCopyToClipboard();

  const handleCopyPassword = async (parent: NewParentDraft) => {
    try {
      await copy(parent.password);
      setCopiedEmail(parent.email);
      setTimeout(() => setCopiedEmail(null), 1500);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  if (parents.length === 0) return null;

  return (
    <div>
      <h5 className={'pb-2 text-sm'}>Konta rodziców</h5>
      <div className={'border-border-primary flex flex-col gap-3 border-t py-2'}>
        <p className={'text-muted-foreground text-xs'}>Utworzono nowe konta rodziców. Skopiuj hasła, aby przekazać je rodzicom.</p>
        <ul className={'flex flex-col gap-2'}>
          {parents.map((parent) => (
            <li key={parent.email} className={'flex items-center gap-3'}>
              <div className={'flex min-w-0 grow flex-col'}>
                <p className={'truncate text-sm'}>
                  {parent.name} {parent.surname}
                </p>
                <p className={'text-muted-foreground truncate text-xs'}>{parent.email}</p>
              </div>
              <Button
                variant={'outline'}
                className="shrink-0"
                onClick={() => handleCopyPassword(parent)}
                disabled={copiedEmail === parent.email}
                aria-label={`Kopiuj hasło ${parent.name} ${parent.surname}`}
              >
                {copiedEmail === parent.email ? <CheckIcon className="text-success" aria-hidden="true" /> : <CopyIcon aria-hidden="true" />}
                Kopiuj hasło
              </Button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
